import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  AbilityEffect,
  CharacterSheetInterface,
  CharacterAction,
  PseudoItem,
  CharacterReceivable,
  CharacterReceivableItem,
  Potence,
} from "@dndModels";
import { DisablableOption } from "@models";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBox";

import ViewItem from "@dndComponents/display/ViewItem";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import { makeStyles } from "@utilities/theme_utilities";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

interface Props {
  char: Character;
  char_interface: CharacterSheetInterface;
  spell: CharacterAbility;
  action: CharacterAction;
  effect: AbilityEffect;
  potence: Potence;
  disabled: boolean;
  log_it?: boolean;
  shared: boolean;
  level: number;
  attack_mod?: number;
  casting_time: string; // 'A' | 'BA' | 'RA' | 'Special' | 'Attack' | "1 Minute" | "10 Minutes" | "1 Hour";
  onChange: (change_types: string[]) => void;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterCreateItemEffect({
  char,
  char_interface,
  spell,
  action,
  effect,
  potence,
  disabled,
  shared,
  level,
  attack_mod = 0,
  onChange,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [selected_id, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        // margin: "4px",
        backgroundColor: theme.palette.secondary.main
      },
    });
  });
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const { classes, cx } = useStyles();

  const renderCharacters = (pseudo_item: PseudoItem, count = 1) => {
    const campaign = char.campaign;
    if (campaign) {
      return campaign.characters.filter(o => o.active && !o.is_archived && o._id !== char._id).map(char => {
        return (
          <Grid key={char._id} size={{
            xs: 12
          }}>
            <div className="padded">
              <Grid container spacing={0} direction="row" className={cx(classes.root, "padded")}>
                <Grid
                  size={{
                    xs: 9
                  }}>
                  { char.name }
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <DandDButtonBox 
                    name="Give"
                    disabled={processing}
                    onClick={() => {
                      const receivable = new CharacterReceivable();
                      receivable.from_id = char._id;
                      receivable.char_id = char._id;
                      receivable.sent_dt = common_util.get_time_number();
                      receivable.items = [new CharacterReceivableItem({
                        type: "Item",
                        obj_id: pseudo_item._id,
                        count: count,
                      })];
                      setProcessing(true);
                      api.upsertObject("character_receivable", receivable).then(res => {
                        setProcessing(false);
                      });
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        );
      });
    }
    return null;
  }
  
  const renderExtras = () => {
    let pseudo_item_ids: string[] = [];
    if (potence.create_item.item_creation_type === "Specific") {
      pseudo_item_ids = potence.create_item.pseudo_item_ids;
    } else if (potence.create_item.item_creation_type === "Knowledge") {
      console.error("Not Implemented");
    } else {
      if (effect.selectable_key !== "") {
        const selected = char_util.getSelectable(char, spell.source_true_id, effect.selectable_key);
        if (selected) {
          pseudo_item_ids = selected;
        } else {
          const cfs = char.all_char_feature_sources.find(o => o.true_id === spell.source_true_id);
          if (cfs) {
            pseudo_item_ids = [cfs.selectable_hash[effect.selectable_key]][0];
          }
        }
      }
    }
    let pseudo_item: PseudoItem | undefined = undefined;
    if (pseudo_item_ids.length === 1) {
      pseudo_item = char_util.getPseudoItem(pseudo_item_ids[0]);
    } else if (pseudo_item_ids.length > 0) {
      pseudo_item = char_util.getPseudoItem(selected_id);
    } else {
      return <>Not Implemented</>;
    }
    
    if (pseudo_item) {
      return (
        <ModalOrDrawer 
          open={ open } 
          full_page
          onClose={() => {
            setOpen(false);
            char.subscription_holder.modalClosed();
          }}>
          { open &&
            <Grid container spacing={0} direction="row" 
              style={{ maxHeight: window.innerHeight - 34 }}>
              <Grid
                className="header centered"
                size={{
                  xs: 12
                }}>
                { `Give ${pseudo_item.name} to Allies` }
              </Grid>
              { renderCharacters(pseudo_item) }
            </Grid>
          }
        </ModalOrDrawer>
      );
    }
    return null;
  }

  const enabled = shared || (!disabled && api.user && (char.owner_id === api.user._id || (char.campaign && char.campaign.owner_id === api.user._id)));
  
  let pseudo_item_ids: string[] = [];
  if (potence.create_item.item_creation_type === "Specific") {
    pseudo_item_ids = potence.create_item.pseudo_item_ids;
  } else if (potence.create_item.item_creation_type === "Knowledge") {
    console.error("Not Implemented");
  } else {
    if (effect.selectable_key !== "") {
      const selected = char_util.getSelectable(char, spell.source_true_id, effect.selectable_key);
      if (selected) {
        pseudo_item_ids = selected;
      } else {
        const cfs = char.all_char_feature_sources.find(o => o.true_id === spell.source_true_id);
        if (cfs) {
          pseudo_item_ids = [cfs.selectable_hash[effect.selectable_key]][0];
        }
      }
    }
  }
  if (pseudo_item_ids.length === 1) {
    const pseudo_item = char_util.getPseudoItem(pseudo_item_ids[0]);
    if (pseudo_item) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 4
            }}>

          </Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 8
            }}>
            <Grid
              size={{
                xs: 6
              }}>
              <ViewItem
                item={pseudo_item}
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <DandDButtonBox
                name={ `Create` }
                disabled={ !enabled }
                onClick={() => {
                  connect_util.addItem({ char, item: pseudo_item }).then(res => {
                    onChange(["Items"]);
                  });
                }} 
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <DandDButtonBox
                name={ `Remove All` }
                disabled={ !enabled }
                onClick={() => {
                  // debugger;
                  // for (const char_item of valid_items.filter(o => 
                  //   o.magic_item_keyword_ids.includes(mik._id))) {
                  //   if (char_item) {
                  //     char_item.sub_char_feature_sources = 
                  //       char_item.sub_char_feature_sources.filter(o => o.source_id !== mik._id);
                  //     // char_item.magic_item_keyword_ids = char_item.magic_item_keyword_ids.filter(o => o !== mik._id);
                  //   }
                  // }
                  onChange(["Items"]);
                }} 
              />
            </Grid>
            { char.campaign &&
              <Grid
                size={{
                  xs: 12
                }}>
                <DandDButtonBox
                  name="Give to Ally"
                  disabled={ !enabled }
                  onClick={() => {
                    setOpen(true);
                  }}
                />
                { renderExtras() }
              </Grid>
            }
          </Grid>
        </Grid>
      );
    }
  } else if (pseudo_item_ids.length > 0) {
    const pseudo_items = char_util.getPseudoItems({ pseudo_item_ids });
    const options = pseudo_items.map(o => {
      const opt: DisablableOption = {
        id: o.id,
        text: o.name
      };
      return opt;
    });
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 4
          }}>

        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 8
          }}>
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox
              name="Select Item"
              options={options}
              value={selected_id}
              onChange={setSelectedId}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <DandDButtonBox
              name={ `Create` }
              disabled={ !enabled || !pseudo_item_ids.includes(selected_id) }
              onClick={() => {
                const pseudo_item = pseudo_items.find(o => o.id === selected_id);
                if (pseudo_item) {
                  connect_util.addItem({ char, item: pseudo_item }).then(res => {
                    onChange(["Items"]);
                  });
                }
              }} 
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <DandDButtonBox
              name={ `Remove All` }
              disabled={ !enabled }
              onClick={() => {
                // debugger;
                // for (const char_item of valid_items.filter(o => 
                //   o.magic_item_keyword_ids.includes(mik._id))) {
                //   if (char_item) {
                //     char_item.sub_char_feature_sources = 
                //       char_item.sub_char_feature_sources.filter(o => o.source_id !== mik._id);
                //     // char_item.magic_item_keyword_ids = char_item.magic_item_keyword_ids.filter(o => o !== mik._id);
                //   }
                // }
                onChange(["Items"]);
              }} 
            />
          </Grid>
          { char.campaign &&
            <Grid
              size={{
                xs: 12
              }}>
              <DandDButtonBox
                name="Give to Ally"
                disabled={ !enabled || !pseudo_item_ids.includes(selected_id) }
                onClick={() => {
                  setOpen(true);
                }}
              />
              { renderExtras() }
            </Grid>
          }
        </Grid>
      </Grid>
    );
  } else {
    return <>Not Implemented</>;
  }
  return <></>;
}
